import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatErrorDetail } from "@/lib/api";
import { roleHomePath } from "@/lib/perms";
import api from "@/lib/api";
import { Lock, ShieldCheck, Building2 } from "lucide-react";
import InstallPWA from "@/components/app/InstallPWA";

export default function Login() {
    const { user, loading, login, loginMfa, activeCompanyId } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [mfaToken, setMfaToken] = useState(null);
    const [code, setCode] = useState("");
    const [sso, setSso] = useState(null);        // {name, slug, enforced} for this email's domain
    const navigate = useNavigate();

    // The IdP hands the session back in the URL fragment — pick it up and sign in.
    useEffect(() => {
        const hash = window.location.hash || "";
        if (hash.includes("sso_access=")) {
            const params = new URLSearchParams(hash.slice(1));
            const access = params.get("sso_access");
            const refresh = params.get("sso_refresh");
            if (access) {
                localStorage.setItem("access_token", access);
                if (refresh) localStorage.setItem("refresh_token", refresh);
                window.location.replace("/");
                return;
            }
        }
        const err = new URLSearchParams(window.location.search).get("sso_error");
        if (err) {
            setError(err);
            window.history.replaceState({}, "", "/login");
        }
    }, []);

    // Does this email's domain use single sign-on?
    useEffect(() => {
        const at = email.indexOf("@");
        if (at < 0 || at === email.length - 1) { setSso(null); return; }
        let cancelled = false;
        const t = setTimeout(() => {
            api.get(`/sso/available?email=${encodeURIComponent(email)}`)
                .then((r) => { if (!cancelled) setSso(r.data?.sso ? r.data : null); })
                .catch(() => { if (!cancelled) setSso(null); });
        }, 350);
        return () => { cancelled = true; clearTimeout(t); };
    }, [email]);

    if (loading) return null;
    if (user) return <Navigate to={roleHomePath(user, activeCompanyId)} replace />;

    const goHome = (u) => navigate(roleHomePath(u, u?.default_company_id));

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setBusy(true);
        try {
            const res = await login(email, password);
            if (res.mfa_required) { setMfaToken(res.mfa_token); setBusy(false); return; }
            goHome(res.user);
        } catch (e) {
            setError(formatErrorDetail(e.response?.data?.detail) || "Login failed");
        } finally { setBusy(false); }
    };

    const submitCode = async (e) => {
        e.preventDefault();
        setError("");
        setBusy(true);
        try {
            const res = await loginMfa(mfaToken, code.trim());
            goHome(res.user);
        } catch (e) {
            setError(formatErrorDetail(e.response?.data?.detail) || "Verification failed");
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            {/* Left brand panel */}
            <div className="hidden lg:flex flex-col justify-between p-10" style={{ background: "hsl(var(--sidebar-bg))", color: "hsl(var(--sidebar-fg))" }}>
                <div className="flex items-center gap-2.5">
                    <img src="/sahod-mark.png" alt="SAHOD" className="size-10" />
                    <div>
                        <div className="font-display font-bold text-white text-lg leading-none">SAHOD</div>
                        <div className="text-[10px] uppercase tracking-widest mt-0.5" style={{ color: "hsl(var(--sidebar-muted))" }}>Salary Automation &amp; HR Ops</div>
                    </div>
                </div>
                <div className="text-[11px]" style={{ color: "hsl(var(--sidebar-muted))" }}>© 2026 SAHOD. All rights reserved.</div>
            </div>

            {/* Right login form */}
            <div className="flex items-center justify-center p-6">
                {mfaToken ? (
                <form onSubmit={submitCode} className="w-full max-w-md surface p-8" data-testid="mfa-form">
                    <div className="flex items-center gap-2 mb-1">
                        <ShieldCheck className="size-5 text-primary" />
                        <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Two-Factor Authentication</div>
                    </div>
                    <h2 className="font-display text-2xl font-bold mt-1">Enter your code</h2>
                    <p className="text-sm text-muted-foreground mt-1">Open your authenticator app and enter the 6-digit code. You can also use a backup code.</p>
                    <div className="mt-6 space-y-3">
                        <input
                            type="text" inputMode="numeric" autoComplete="one-time-code" autoFocus
                            value={code} onChange={(e) => setCode(e.target.value)}
                            placeholder="123 456"
                            className="w-full h-12 px-3 rounded-md border border-border text-center text-2xl tracking-[0.3em] font-mono focus:outline-none focus:ring-2 focus:ring-primary/30"
                            data-testid="mfa-code-input"
                        />
                        {error && <div className="text-sm text-destructive" data-testid="mfa-error">{error}</div>}
                        <button type="submit" disabled={busy}
                            className="w-full h-10 rounded-md bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-60 inline-flex items-center justify-center gap-2"
                            data-testid="mfa-submit-btn">
                            <Lock className="size-4" />{busy ? "Verifying…" : "Verify & sign in"}
                        </button>
                        <button type="button" onClick={() => { setMfaToken(null); setCode(""); setError(""); }}
                            className="w-full h-9 text-sm text-muted-foreground hover:text-foreground">
                            ← Back to sign in
                        </button>
                    </div>
                </form>
                ) : (
                <form onSubmit={submit} className="w-full max-w-md surface p-8" data-testid="login-form">
                    <div className="flex items-center gap-2 mb-1">
                        <ShieldCheck className="size-5 text-primary" />
                        <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Secure Sign-in</div>
                    </div>
                    <h2 className="font-display text-2xl font-bold mt-1">Sign in to your workspace</h2>
                    <p className="text-sm text-muted-foreground mt-1">Use your enterprise credentials.</p>

                    <div className="mt-6 space-y-3">
                        <div>
                            <label className="text-xs font-medium text-muted-foreground">Email</label>
                            <input
                                type="email"
                                placeholder="you@company.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full h-10 px-3 mt-1 rounded-md border border-border focus:outline-none focus:ring-2 focus:ring-primary/30"
                                required
                                data-testid="login-email-input"
                            />
                        </div>
                        {sso && (
                            <div className="rounded-md border border-primary/30 bg-primary/5 p-3" data-testid="sso-panel">
                                <div className="text-xs text-muted-foreground mb-2">
                                    <b className="text-foreground">{sso.name}</b> manages sign-in for this email domain.
                                </div>
                                <a href={`/api/sso/start/${sso.slug}`}
                                   className="w-full h-10 rounded-md bg-primary text-primary-foreground font-medium inline-flex items-center justify-center gap-2"
                                   data-testid="sso-continue">
                                    <Building2 className="size-4" /> Continue with {sso.name}
                                </a>
                                {!sso.enforced && <div className="text-[11px] text-muted-foreground text-center mt-2">or sign in with your password below</div>}
                            </div>
                        )}
                        <div className={sso?.enforced ? "hidden" : ""}>
                            <label className="text-xs font-medium text-muted-foreground">Password</label>
                            <input
                                type="password"
                                placeholder="Your password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full h-10 px-3 mt-1 rounded-md border border-border focus:outline-none focus:ring-2 focus:ring-primary/30"
                                required
                                data-testid="login-password-input"
                            />
                        </div>
                        {error && <div className="text-sm text-destructive" data-testid="login-error">{error}</div>}
                        <button
                            type="submit"
                            disabled={busy}
                            className={`w-full h-10 rounded-md bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-60 inline-flex items-center justify-center gap-2 ${sso?.enforced ? "hidden" : ""}`}
                            data-testid="login-submit-btn"
                        >
                            <Lock className="size-4" />
                            {busy ? "Signing in…" : "Sign in"}
                        </button>
                    </div>
                    <div className="mt-6 text-xs text-muted-foreground border-t pt-4">
                        <div className="mt-3"><InstallPWA /></div>
                    </div>
                </form>
                )}
            </div>
        </div>
    );
}
